import React, { useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  Chip,
  FormControlLabel,
  IconButton,
  MenuItem,
  Select,
  TextField,
  Typography,
  CircularProgress,
  Paper,
  Grid,
  Collapse,
  OutlinedInput,
  ListItemText,
  InputLabel,
  FormControl,
} from "@mui/material";
import { TimePicker } from "@mui/x-date-pickers/TimePicker";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import CloseIcon from "@mui/icons-material/Close";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import dayjs from "dayjs";
import Sidebar from "../Navigation/Sidebar/Sidebar";
import { toastr } from "react-redux-toastr";
import { toast } from "react-toastify";
import SuccessDialog from "./SuccessDialog";
import { ArrowBack } from "@mui/icons-material";
import { Link } from "react-router-dom";

const days = ["MONDAY", "TUESDAY", "WEDNESDAY", "THURSDAY", "FRIDAY", "SATURDAY", "SUNDAY"];
const shortDays = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
};

const CreateRoster = ({ onPayloadReady, openDialog, agentListData, loading }) => {
  const [selectedAgents, setSelectedAgents] = useState([]);
  const [startDate, setStartDate] = useState(dayjs());
  const [timeSlots, setTimeSlots] = useState([
    { from: null, to: null, days: [], active: true, expanded: true }
  ]);

  console.log("agentListData in component:", agentListData);

  // Handle agent selection from dropdown
  const handleAgentChange = (event) => {
    const {
      target: { value },
    } = event;
    setSelectedAgents(
      typeof value === 'string' ? value.split(',') : value,
    );
  };

  // Select all agents
  const handleSelectAllAgents = () => {
    if (!agentListData || agentListData.length === 0) return;
    
    if (selectedAgents.length === agentListData.length) {
      setSelectedAgents([]);
    } else {
      const allAgentEmails = agentListData
        .filter(agent => getAgentEmail(agent))
        .map((agent) => getAgentEmail(agent));
      setSelectedAgents(allAgentEmails);
    }
  };

  // Handle time change
  const handleTimeChange = (index, type, value) => {
    setTimeSlots((prev) =>
      prev.map((slot, i) => (i === index ? { ...slot, [type]: value } : slot))
    );
  };

  // Handle day toggle
  const handleDayToggle = (index, day) => {
    setTimeSlots((prev) =>
      prev.map((slot, i) => {
        if (i !== index) return slot;
        const newDays = slot.days.includes(day)
          ? slot.days.filter((d) => d !== day)
          : [...slot.days, day];
        return { ...slot, days: newDays };
      })
    );
  };

  // Handle active toggle
  const handleActiveToggle = (index) => {
    setTimeSlots((prev) =>
      prev.map((slot, i) =>
        i === index ? { ...slot, active: !slot.active } : slot
      )
    );
  };

  // Toggle slot expansion
  const handleToggleSlot = (index) => {
    setTimeSlots((prev) =>
      prev.map((slot, i) =>
        i === index ? { ...slot, expanded: !slot.expanded } : slot
      )
    );
  };

  // Add new slot
  const handleAddSlot = () => {
    setTimeSlots((prev) => [
      ...prev,
      { from: null, to: null, days: [], active: true, expanded: true }
    ]);
  };

  // Remove slot
  const handleRemoveSlot = (index) => {
    setTimeSlots((prev) => prev.filter((_, i) => i !== index));
  };

  // Validation: at least one agent selected and at least one valid slot
  const isFormValid = () => {
    return (
      selectedAgents.length > 0 &&
      timeSlots.some((slot) => slot.from && slot.to && slot.days.length > 0) &&
      startDate
    );
  };

  const isSlotValid = (slot) => {
    return slot.from && slot.to && slot.days.length > 0;
  };

  // Save handler
  const handleSave = () => {
    for (let i = 0; i < timeSlots.length; i++) {
      if (!isSlotValid(timeSlots[i])) {
        toastr.error(`Please complete slot ${i + 1}`, {
          position: toast.POSITION.TOP_RIGHT,
          autoClose: 1000,
        });
        return;
      }
    }

    const formattedTimeSlots = timeSlots.map((slot) => ({
      startTime: slot.from ? dayjs(slot.from).format("HH:mm:ss") : null,
      endTime: slot.to ? dayjs(slot.to).format("HH:mm:ss") : null,
      days: slot.days,
      isActive: slot.active,
    }));

    const payload = {
      agentEmails: selectedAgents,
      startDate: startDate ? startDate.format("YYYY-MM-DD") : null,
      agentTime: formattedTimeSlots
    };

    console.log("📦 Final Payload:", JSON.stringify(payload, null, 2));

    if (onPayloadReady) {
      onPayloadReady(payload);
    }
  };

  const getAgentEmail = (agent) => {
    return agent.agent_email || agent.email || agent;
  };

  const hasAgents = agentListData && Array.isArray(agentListData) && agentListData.length > 0;
  const allAgentEmails = hasAgents ? agentListData.map(agent => getAgentEmail(agent)) : [];

  return (
    <div className="main-div">
      <div>
        <Sidebar />
      </div>

      {openDialog && <SuccessDialog />}
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <Box p={4} sx={{ width: "100%", maxWidth: "1200px", margin: "0 auto", height: "calc(100vh - 80px)" }}>
          <Typography
            variant="h6"
            fontWeight="bold"
            mb={2}
            display="flex"
            alignItems="center"
          >
            <Link to="/cst_rooster">
              <ArrowBack sx={{ color: "black", mr: 1 }} />
            </Link>
            Create Roster
          </Typography>

          {/* Agent Selection - Dropdown with Checkboxes */}
          <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
            <Typography fontWeight={600} mb={2}>
              Select Agents
            </Typography>
            
            {loading && !agentListData ? (
              <Box display="flex" justifyContent="center" p={3}>
                <CircularProgress size={30} />
                <Typography sx={{ ml: 2 }}>Loading agents...</Typography>
              </Box>
            ) : !hasAgents ? (
              <Typography color="error" p={2}>
                No agents available. Please add agents first.
              </Typography>
            ) : (
              <FormControl fullWidth>
                <InputLabel id="agent-select-label">Select Agents</InputLabel>
                <Select
                  labelId="agent-select-label"
                  id="agent-select"
                  multiple
                  value={selectedAgents}
                  onChange={handleAgentChange}
                  input={<OutlinedInput label="Select Agents" />}
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {selected.map((value) => (
                        <Chip key={value} label={value} size="small" />
                      ))}
                    </Box>
                  )}
                  MenuProps={MenuProps}
                  disabled={loading}
                >
                  {allAgentEmails.map((email) => (
                    <MenuItem key={email} value={email}>
                      <Checkbox checked={selectedAgents.indexOf(email) > -1} />
                      <ListItemText primary={email} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          </Paper>

          {/* Time Slots Section with proper scrolling */}
          <Paper elevation={2} sx={{ p: 3, height: "calc(84vh - 280px)", display: "flex", flexDirection: "column" }}>
            <Typography fontWeight={600} mb={2}>
              Schedule Time Slots
            </Typography>

            {/* Scrollable content */}
            <Box sx={{ flex: 1, overflowY: "auto", mb: 2 }}>
              <Box sx={{ mb: 2, marginTop:"10px" }}>
                <DatePicker
                  label="Select Start Date"
                  value={startDate}
                  onChange={(newValue) => setStartDate(newValue)}
                  minDate={dayjs()}
                  disabled={loading}
                  sx={{ width: "100%" }}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      InputProps: {
                        startAdornment: <CalendarTodayIcon sx={{ mr: 1, color: "#14AE78" }} />
                      }
                    }
                  }}
                />
              </Box>

              {timeSlots.map((slot, index) => (
                <Box
                  key={index}
                  mb={2}
                  border="1px solid #e0e0e0"
                  borderRadius={2}
                  sx={{ backgroundColor: "#fafafa" }}
                >
                  {/* Slot Header */}
                  <Box
                    p={2}
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                    sx={{ cursor: "pointer", borderBottom: slot.expanded ? "1px solid #e0e0e0" : "none" }}
                    onClick={() => handleToggleSlot(index)}
                  >
                    <Box display="flex" alignItems="center" gap={2}>
                      <Typography fontWeight={500}>
                        Time Slot {index + 1}
                      </Typography>
                      {slot.from && slot.to && (
                        <Chip
                          label={`${dayjs(slot.from).format("HH:mm")} - ${dayjs(slot.to).format("HH:mm")}`}
                          size="small"
                          color="primary"
                          variant="outlined"
                        />
                      )}
                      {slot.days.length > 0 && (
                        <Chip
                          label={`${slot.days.length} day(s) selected`}
                          size="small"
                          color="info"
                          variant="outlined"
                        />
                      )}
                    </Box>
                    <Box display="flex" alignItems="center" gap={1}>
                      {timeSlots.length > 1 && (
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRemoveSlot(index);
                          }}
                          sx={{ color: "#f44336" }}
                        >
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      )}
                      {slot.expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    </Box>
                  </Box>

                  {/* Slot Content - Only show when expanded */}
                  <Collapse in={slot.expanded}>
                    <Box p={3}>
                      {/* Time Pickers */}
                      <Box display="flex" gap={2} flexWrap="wrap" mb={2}>
                        <TimePicker
                          label="From"
                          value={slot.from}
                          onChange={(val) => handleTimeChange(index, "from", val)}
                          disabled={loading}
                          sx={{ flex: 1, minWidth: "150px" }}
                          slotProps={{ textField: { size: "small" } }}
                        />
                        <TimePicker
                          label="To"
                          value={slot.to}
                          onChange={(val) => handleTimeChange(index, "to", val)}
                          disabled={loading}
                          sx={{ flex: 1, minWidth: "150px" }}
                          slotProps={{ textField: { size: "small" } }}
                        />
                      </Box>

                      {/* Day Chips */}
                      <Box mb={2}>
                        <Typography fontSize={14} fontWeight={500} mb={1}>
                          Select Days
                        </Typography>
                        <Box display="flex" gap={1} flexWrap="wrap">
                          {days.map((day, idx) => (
                            <Chip
                              key={day}
                              label={shortDays[idx]}
                              variant={
                                slot.days.includes(day) ? "filled" : "outlined"
                              }
                              color={
                                slot.days.includes(day) ? "primary" : "default"
                              }
                              onClick={() => handleDayToggle(index, day)}
                              clickable
                              disabled={loading}
                            />
                          ))}
                        </Box>
                      </Box>

                      {/* Active Toggle */}
                      <Box>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={slot.active}
                              onChange={() => handleActiveToggle(index)}
                              color="success"
                              disabled={loading}
                            />
                          }
                          label="Active"
                        />
                      </Box>
                    </Box>
                  </Collapse>
                </Box>
              ))}
            </Box>

            {/* Add Slot Button - Fixed at bottom */}
            <Box mt={2}>
              <Button
                onClick={handleAddSlot}
                variant="outlined"
                color="success"
                disabled={loading}
                sx={{ borderRadius: "2rem", textTransform: "none" }}
              >
                + Add Another Time Slot
              </Button>
            </Box>
          </Paper>

          {/* Save Button */}
          <Box mt={3} textAlign="center">
            <Button
              variant="contained"
              color="primary"
              disabled={!isFormValid() || loading}
              onClick={handleSave}
              sx={{
                borderRadius: "2rem",
                padding: "10px 40px",
                fontSize: "1rem",
                backgroundColor: "#14AE78",
                "&:hover": { backgroundColor: "#0e9c68" },
                "&.Mui-disabled": { backgroundColor: "#ccc" }
              }}
            >
              {loading ? <CircularProgress size={24} color="inherit" /> : "Create Roster"}
            </Button>
          </Box>
        </Box>
      </LocalizationProvider>
    </div>
  );
};

export default CreateRoster;